// Pure placement + spotlight-geometry math for the tour overlay. No React, no
// framer-motion, no DOM writes — every function here is a deterministic
// transform of numbers, so the overlay's per-frame rAF loop (D4) can call them
// without allocating renders. The overlay owns all statefulness (springs, refs);
// this module owns the geometry.

export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Placement = Side | 'center';

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  w: number;
  h: number;
}

export interface CardPlacement {
  x: number;
  y: number;
  side: Placement;
}

// Breathing room between the target and the spotlight cutout edge (kept from v2).
export const SPOTLIGHT_PADDING = 8;

// Viewport margin the card must stay within, and the gap it keeps from the hole.
const MARGIN = 16;
const GAP = 12;

// Largest corner radius the spotlight hole ever uses.
const MAX_HOLE_RADIUS = 12;

export function clampToViewport(rect: Rect, vp: Size): Rect {
  const top = Math.max(0, rect.top);
  const left = Math.max(0, rect.left);
  const bottom = Math.min(vp.h, rect.top + rect.height);
  const right = Math.min(vp.w, rect.left + rect.width);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

export function padRect(rect: Rect, pad: number): Rect {
  return {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
}

function opposite(side: Side): Side {
  switch (side) {
    case 'top':
      return 'bottom';
    case 'bottom':
      return 'top';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
  }
}

// Choose where the card sits relative to the (padded, viewport-clamped) target.
// Candidates are tried in order — prevSide first, which is the hysteresis that
// keeps the card on its current side until that side genuinely stops fitting,
// instead of flip-flopping frame-to-frame at a fit boundary. No candidate fits
// (tiny viewport, viewport-filling target) → dead-center over the spotlight.
export function computeCardPlacement(
  target: Rect,
  card: Size,
  preferred: Side,
  prevSide: Side | null,
  vp: Size
): CardPlacement {
  const cx = target.left + target.width / 2;
  const cy = target.top + target.height / 2;
  const targetTop = target.top;
  const targetLeft = target.left;
  const targetBottom = target.top + target.height;
  const targetRight = target.left + target.width;

  const clampX = (x: number) => Math.min(Math.max(x, MARGIN), vp.w - card.w - MARGIN);
  const clampY = (y: number) => Math.min(Math.max(y, MARGIN), vp.h - card.h - MARGIN);

  const pos = (side: Side): { x: number; y: number } => {
    switch (side) {
      case 'bottom':
        return { x: clampX(cx - card.w / 2), y: targetBottom + GAP };
      case 'top':
        return { x: clampX(cx - card.w / 2), y: targetTop - GAP - card.h };
      case 'right':
        return { x: targetRight + GAP, y: clampY(cy - card.h / 2) };
      case 'left':
        return { x: targetLeft - GAP - card.w, y: clampY(cy - card.h / 2) };
    }
  };

  const fits = (p: { x: number; y: number }) =>
    p.x >= MARGIN &&
    p.x + card.w <= vp.w - MARGIN &&
    p.y >= MARGIN &&
    p.y + card.h <= vp.h - MARGIN;

  const candidates: Side[] = [];
  const consider = (side: Side | null) => {
    if (side && !candidates.includes(side)) candidates.push(side);
  };
  consider(prevSide);
  consider(preferred);
  consider(opposite(preferred));
  consider('bottom');
  consider('top');
  consider('right');
  consider('left');

  for (const side of candidates) {
    const p = pos(side);
    if (fits(p)) return { x: p.x, y: p.y, side };
  }

  return { x: (vp.w - card.w) / 2, y: (vp.h - card.h) / 2, side: 'center' };
}

// A target that fills most of the viewport leaves no side for the card and gets
// a page-sized spotlight with a centered card (unchanged threshold from v2).
export function isHugeTarget(clamped: Rect, vp: Size): boolean {
  return clamped.height > 0.7 * vp.h && clamped.width > 0.5 * vp.w;
}

// Compose the even-odd scrim path: outer subpath = the whole viewport, inner
// subpath = a rounded-rect hole over the target. With fill-rule="evenodd" the
// hole punches through. When the hole has collapsed (< 1px on either axis) the
// inner subpath is omitted, leaving a full scrim — the centered/no-target modes.
export function composeHolePath(
  hx: number,
  hy: number,
  hw: number,
  hh: number,
  vw: number,
  vh: number
): string {
  const outer = `M0 0H${vw}V${vh}H0Z`;
  if (hw < 1 || hh < 1) return outer;
  const r = Math.min(MAX_HOLE_RADIUS, hw / 2, hh / 2);
  const hole =
    ` M${hx + r} ${hy}` +
    `h${hw - 2 * r}` +
    `a${r} ${r} 0 0 1 ${r} ${r}` +
    `v${hh - 2 * r}` +
    `a${r} ${r} 0 0 1 ${-r} ${r}` +
    `h${-(hw - 2 * r)}` +
    `a${r} ${r} 0 0 1 ${-r} ${-r}` +
    `v${-(hh - 2 * r)}` +
    `a${r} ${r} 0 0 1 ${r} ${-r}` +
    `Z`;
  return outer + hole;
}
